import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import { refreshCsrfToken } from "../helpers/csrf";

// Connects to data-controller="inline-image-upload"
// Handles inline image uploads that open file explorer directly and submit form after upload
export default class extends Controller {
  static targets = ["input", "form"];
  static values = {
    url: { type: String, default: "/rails/active_storage/direct_uploads" },
    maxFileSize: { type: Number, default: 20 * 1024 * 1024 }, // 20MB default
    errorInvalidType: { type: String, default: "Please select an image file." },
    errorTooLarge: { type: String, default: "File is too large. Maximum size: %{size}MB." },
    errorUploadFailed: { type: String, default: "Upload failed. Please try again." }
  };

  connect() {
    this.isUploading = false;
    this._boundSubmitEndHandler = null;
  }

  triggerFileInput() {
    this.inputTarget.click();
  }

  async upload() {
    // Prevent double uploads - set flag immediately
    if (this.isUploading) return;
    this.isUploading = true;

    const file = this.inputTarget.files[0];
    if (!file) {
      this.isUploading = false;
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      this.showError(this.errorInvalidTypeValue);
      this.clearFileInput();
      this.isUploading = false;
      return;
    }

    // Validate file size
    if (file.size > this.maxFileSizeValue) {
      const maxSizeMB = Math.round(this.maxFileSizeValue / (1024 * 1024));
      this.showError(this.errorTooLargeValue.replace("%{size}", maxSizeMB));
      this.clearFileInput();
      this.isUploading = false;
      return;
    }

    // Show loading state
    this.element.classList.add("uploading");

    try {
      await refreshCsrfToken();
    } catch (error) {
      console.error("CSRF refresh failed:", error);
      this.showError(this.errorUploadFailedValue);
      this.resetUploadState();
      return;
    }

    const upload = new DirectUpload(file, this.urlValue);

    upload.create((error, blob) => {
      // Check if controller is still connected before processing callback
      if (!this.element.isConnected) return;

      if (error) {
        console.error("Upload error:", error);
        this.showError(this.errorUploadFailedValue);
        this.resetUploadState();
      } else {
        // Create hidden field with signed blob id
        const hiddenField = document.createElement("input");
        hiddenField.type = "hidden";
        hiddenField.name = this.inputTarget.name;
        hiddenField.value = blob.signed_id;
        this.formTarget.appendChild(hiddenField);

        // Clear the file input to prevent sending both file and signed_id
        this.inputTarget.value = "";

        // Store handler reference for cleanup
        this._boundSubmitEndHandler = () => {
          this.resetUploadState();
        };

        // Listen for form submission completion to reset state
        this.formTarget.addEventListener("turbo:submit-end", this._boundSubmitEndHandler, { once: true });

        // Submit the form
        this.formTarget.requestSubmit();
      }
    });
  }

  resetUploadState() {
    this.element.classList.remove("uploading");
    this.isUploading = false;
    this.clearFileInput();
    this._boundSubmitEndHandler = null;
  }

  clearFileInput() {
    this.inputTarget.value = "";
  }

  showError(message) {
    alert(message);
  }

  disconnect() {
    // Clean up event listener if still attached
    if (this._boundSubmitEndHandler && this.hasFormTarget) {
      this.formTarget.removeEventListener("turbo:submit-end", this._boundSubmitEndHandler);
    }
    this.resetUploadState();
  }
}
